import React, { useState } from "react";

import { Close } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Popover,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { toBlob } from "html-to-image";
import { enqueueSnackbar } from "notistack";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";

import { PostPreviewCard } from "../../common";
import CustomDateTimePicker from "../../common/CustomDateTimePicker";
import { setActionButtonToggle as setActionButtonToggleAction } from "../../redux/slices/authSlice";
import {
  createPost as createPostAction,
  sharePostNowAiApi as sharePostNowAiApiAction,
  updatePost as updatePostAction,
} from "../../redux/slices/postsSlice";
import { uploadFileToFirebase } from "../../utils/firebase_uploader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { lg: "35vw", md: "50vw", sm: "80vw", xs: "95vw" },
  // height: "88vh",
  maxHeight: "96vh",
  overflow: "auto",
  scrollbarWidth: "thin",
  bgcolor: "#FBFBFB",
  boxShadow: 24,
  outline: "none",
  borderRadius: "5px",
};

const PostPreviewModal = ({
  open,
  user,
  company,
  post_form_data,
  onClose,
  createPost,
  setActionButtonToggle,
  onPostSuccess,
  updatePost,
  flag,
  sharePostNowAiApi,
}) => {
  const { flow } = useParams();
  const isSocialConnected = user?.socials?.linkedin?.page;
  const [scheduledDate, setScheduledDate] = useState(dayjs());
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const openPopover = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCreatePost = async (scheduleTime) => {
    if (!isSocialConnected) {
      setActionButtonToggle(true);
      onClose();
      return enqueueSnackbar("You haven't connected your linkedIn yet", {
        variant: "info",
      });
    }

    try {
      setLoading(scheduleTime ? "schedule" : "now");

      let payload = { ...post_form_data };

      const templateData = post_form_data?.company_personalised_template_url;

      if (typeof templateData === "string") {
        payload.company_personalised_template_url = templateData;
      } else if (templateData?.id) {
        const element = document.getElementById(templateData?.id);
        if (!element) throw new Error("Template element not found in DOM");

        const blob = await toBlob(element, {
          width: 800,
          height: 800,
          cacheBust: true,
          useCORS: true,
          filter: (node) => {
            if (node.tagName === "LINK") {
              console.warn("Ignoring external stylesheet:", node.href);
              return false;
            }
            return true;
          },
        });

        const templateUrl = await uploadFileToFirebase(
          blob,
          "personalized_templates"
        );
        payload.company_personalised_template_url = templateUrl;
      }

      const title = payload?.title;
      payload.job_name = title
        ? title
        : `${company?.company_name || "Default"}-JOB`;

      delete payload?.prompt;
      payload.job_criteria = post_form_data?.job_criteria?.map((c) => ({
        icp_weightage: c?.icp_weightage,
        name: c?.name,
        progress: c?.progress,
        value: c?.value,
        compulsory: !!c?.compulsory,
      }));

      const region = Intl.DateTimeFormat().resolvedOptions().timeZone;

      payload.recruiter_id = user?.id;
      payload.company_id = company?.id;
      payload.job_status = scheduleTime ? "scheduled" : "active";
      payload.posting_time = scheduleTime || dayjs().format("l h:mm A");
      payload.region = region;
      payload.flow = flow;

      await createPost({
        data: payload,
        cb: () => onPostSuccess(payload.job_status),
      });
    } catch (e) {
      console.log("ERROR WHILE POSTING", e);
    } finally {
      setLoading(false);
    }
  };
  
  const _handleUpdatePost = async () => {
    setLoading("now");
    try {
      let payload = {
        id: post_form_data?.id,
        job_description: post_form_data?.job_description,
        region: post_form_data?.region,
      };

      const templateData = post_form_data?.company_personalised_template_url;

      if (typeof templateData === "string") {
        // Use direct image URL
        payload.company_personalised_template_url = templateData;
      } else if (templateData?.id) {
        // Convert React component to image
        const element = document.getElementById(templateData.id);

        if (!element) throw new Error("Template element not found in DOM");

        const blob = await toBlob(element, {
          width: 800,
          height: 800,
          filter: (node) => node.tagName !== "LINK",
          cacheBust: true,
          useCORS: true,
        });

        if (blob) {
          const templateUrl = await uploadFileToFirebase(
            blob,
            "personalized_templates"
          );
          payload.company_personalised_template_url = templateUrl;
        }
      }

      console.log(payload);

      await updatePost({
        data: payload,
        cb: () => {
          _handlePostNow();
          onPostSuccess("active");
        },
      });
    } catch (e) {
      console.log(e);
      enqueueSnackbar(`${e?.message || e}`, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const _handlePostNow = async () => {
    try {
      console.log("Enter here in handlePostnow");

      const currentDate = new Date();
      const futureDate = new Date(
        currentDate.setDate(currentDate.getDate() + 7)
      ).toISOString();

      const payload = {
        job_id: post_form_data?.id,
        is_schedule: false,
        region: post_form_data?.region,
        posting_time: dayjs().format("l h:mm A"),
        repost: true,
        expiry_date: futureDate,
      };

      await sharePostNowAiApi({
        data: payload,
        repost: true,
        cb: () => onClose(),
      });
    } catch (error) {
      console.log(error);
    }
  };

  console.log("flag", flag);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!loading) onClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Box
            display="flex"
            justifyContent="space-between"
            py="1rem"
            px="1.5rem"
            alignItems="center"
            bgcolor="white"
            borderBottom="1px solid #E8EAED"
          >
            <Typography fontSize="22px" fontWeight={600}>
              Post Preview
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
          <PostPreviewCard
            template={post_form_data?.company_personalised_template_url}
            postContent={post_form_data?.job_description}
            cardStyles={{
              borderColor: "#4BC7E1",
              maxWidth: "25vw",
              mx: "auto",
              px: "2rem",
              borderRadius: "13px",
              py: { xs: 2, lg: "1rem" },
            }}
          />

          <Box display="flex" justifyContent="center" gap={2} my={2} px={3}>
            {!flag && (
              <Button
                disabled={loading === "schedule"}
                variant="outlined"
                sx={{
                  borderColor: "#999",
                  color: "#999",
                  borderRadius: "40px",
                  textTransform: "none",
                  px: 4,
                  py: 1,
                }}
                onClick={handleClick}
              >
                {loading === "schedule" ? (
                  <CircularProgress sx={{ color: "white" }} size={18} />
                ) : (
                  "Schedule a post"
                )}
              </Button>
            )}
            <Button
              disabled={loading === "now"}
              variant="main"
              onClick={() => {
                if (flag) {
                  console.log("FIrst update and save template then post job");
                  _handleUpdatePost();
                } else {
                  handleCreatePost();
                }
              }}
            >
              {loading === "now" ? (
                <CircularProgress sx={{ color: "white" }} size={18} />
              ) : (
                "Share Now"
              )}
            </Button>
          </Box>

          <Popover
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  width: 10,
                  height: 10,
                  bgcolor: "#FBFBFB",
                  transform: "translate(-50%, -50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
          >
            <CustomDateTimePicker
              value={scheduledDate}
              minDate={dayjs()}
              isLoading={loading}
              onChange={(date) => setScheduledDate(date)}
              onAccept={(date) => {
                setScheduledDate(date);
                handleCreatePost(date.format("l h:mm A"));
              }}
            />
          </Popover>
        </Box>
      </Fade>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  company: state.company?.company,
  post_form_data: state.posts?.post_form_data,
});

const mapDispatchToProps = {
  createPost: createPostAction,
  updatePost: updatePostAction,
  sharePostNowAiApi: sharePostNowAiApiAction,
  setActionButtonToggle: setActionButtonToggleAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PostPreviewModal);
